import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { customerBaseUrl } from '../runtime-env';

// 后端统一走 customer(BFF)，customer 内部经 gRPC 聚合 identity KYB。identity 不直接对外。
// 注意：onboarding 走登录态(JWT，拦截器自动注入 Bearer)，不经 gateway 的 AK/SK 数据面——
// 企业开户完成后才在「API Keys」页签发 AK/SK，那之后才用 gateway 做服务端自动化。

export interface StartOnboardingBody {
  contact_email: string;
  contact_phone: string;
  incorporation_country: string;
  business_type: number;
  success_url: string;
  reject_url: string;
}

// ── 完整 KYB 公司资料表单（请求体；Kratos protojson 输入接受 snake_case）──

export interface AddressBody {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface TaxIdentifiersBody {
  ein: string;
  vat_number: string;
  unified_social_credit_code: string;
  business_registration_number: string;
  other_tax_id: string;
  other_tax_id_type: string;
}

export interface VolumeEstimateBody {
  currency: string;
  monthly_volume_min: number;
  monthly_volume_max: number;
  average_transaction_amount: number;
  highest_transaction_amount: number;
}

export interface ListedCompanyInfoBody {
  exchange: string;
  ticker: string;
  exempted: boolean;
}

export interface CompanyProfileBody {
  legal_name: string;
  doing_business_as: string;
  business_type: number;
  incorporation_country: string;
  incorporation_state: string;
  incorporation_date: string;
  registration_number: string;
  tax_identifiers: TaxIdentifiersBody;
  registered_address: AddressBody;
  operating_address: AddressBody;
  phone: string;
  website: string;
  support_email: string;
  mcc: string;
  industry_category: string;
  product_description: string;
  processing_countries: string;
  volume_estimate: VolumeEstimateBody;
  listed_company_info: ListedCompanyInfoBody;
}

export interface BeneficialOwnerBody {
  person_id: string; // 编辑现有人员时原样回传（后端据此定位并更新）；新增行留空，后端据此走新增
  first_name: string;
  last_name: string;
  middle_name: string;
  date_of_birth: string;
  nationality: string;
  email: string;
  phone: string;
  title: string;
  ownership_percent: number;
  is_beneficial_owner: boolean;
  is_director: boolean;
  is_legal_representative: boolean;
  is_representative: boolean; // 档案管理员（代表人）
}

// 股权树节点/边（思维导图录入）：client_id 为前端本地 id，落库时 identity 重分配持久化 id。
export interface CorporationOwnershipNodeBody {
  client_id: string;
  kind: number; // 1=申请主体 2=法人股东 3=自然人
  name: string;
  first_name: string; // 自然人 名
  last_name: string; // 自然人 姓
  person_id: string;
  country: string;
  registration_number: string;
  entity_type: string;
}
export interface CorporationOwnershipEdgeBody {
  owner_client_id: string;
  owned_client_id: string;
  ownership_percent: number;
}

export interface SaveDraftBody {
  application_id: string;
  contact_email: string;
  contact_phone: string;
  company: CompanyProfileBody;
  beneficial_owners: BeneficialOwnerBody[];
  no_beneficial_owners: boolean;
  ownership_nodes?: CorporationOwnershipNodeBody[];
  ownership_edges?: CorporationOwnershipEdgeBody[];
}

export interface SubmitBody extends SaveDraftBody {
  ownership_confirmed: boolean;
  success_url: string;
  reject_url: string;
}

// ── 响应（customer BFF Kratos protojson 响应字段为 camelCase）──

// 单项 ID&V 核验任务（公司维度或某人员维度）。
export interface VerificationTask {
  scope: string; // company | person
  personId: string;
  name: string;
  verificationUrl: string;
  status: string; // verified / pending / failed / unverified
  ownershipPercent: number;
  role: string;
  required: boolean;
}

export interface OnboardingReply {
  applicationId: string;
  verificationUrl: string;
  status: string;
  provider: string;
  tasks?: VerificationTask[];
}

// 自助生成入驻链接请求体（注册国/业务类型可选预填，可空；snake_case，Kratos protojson 输入）。
export interface CreateMyLinkBody {
  incorporation_country?: string;
  business_type?: number;
  application_id?: string; // 续填：绑定已有草稿而非新建
}

// 自助生成入驻链接响应（Kratos protojson camelCase）。
export interface MyOnboardingLinkReply {
  onboardingUrl: string;
  token: string;
  applicationId: string;
}

export interface OnboardingVerification {
  legalEntity: string;
  taxId: string;
  address: string;
  documents: string;
  aml: string;
  sanctions: string;
}

export interface OnboardingStatus {
  applicationId: string;
  status: string; // 验证聚合态（verified/pending/failed）
  applicationStatus?: string; // 申请生命周期态（approved/rejected/corporation_in_review/draft…）
  provider: string;
  verification?: OnboardingVerification;
  tasks?: VerificationTask[];
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private http = inject(HttpClient);

  start(body: StartOnboardingBody): Promise<OnboardingReply> {
    return firstValueFrom(
      this.http.post<OnboardingReply>(`${customerBaseUrl()}/v1/customer/onboarding/start`, body),
    );
  }

  // 为当前登录用户自助生成链接式独立入驻会话，返回免登录链接（前端据此跳转到独立入驻应用）。
  createMyLink(body: CreateMyLinkBody): Promise<MyOnboardingLinkReply> {
    return firstValueFrom(
      this.http.post<MyOnboardingLinkReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/my-link`,
        body,
      ),
    );
  }

  // 保存企业资料草稿（按需建申请，不发起验证）。
  saveDraft(body: SaveDraftBody): Promise<OnboardingReply> {
    return firstValueFrom(
      this.http.post<OnboardingReply>(`${customerBaseUrl()}/v1/customer/onboarding/draft`, body),
    );
  }

  // 提交申请：落最终资料 + 同步受益人 + 为公司及全部必核验人发起 ID&V 会话，返回逐项任务。
  submitApplication(body: SubmitBody): Promise<OnboardingReply> {
    return firstValueFrom(
      this.http.post<OnboardingReply>(`${customerBaseUrl()}/v1/customer/onboarding/submit`, body),
    );
  }

  status(applicationId: string): Promise<OnboardingStatus> {
    return firstValueFrom(
      this.http.get<OnboardingStatus>(
        `${customerBaseUrl()}/v1/customer/onboarding/${encodeURIComponent(applicationId)}/status`,
      ),
    );
  }

  // 按「当前登录用户」解析其入驻状态（后端按归属人找最新申请），不依赖本地缓存的 application_id。
  // 用户暂无申请时后端返回空 status。
  myStatus(): Promise<OnboardingStatus> {
    return firstValueFrom(
      this.http.get<OnboardingStatus>(`${customerBaseUrl()}/v1/customer/onboarding/status`),
    );
  }

  // 回显现有申请的完整资料（公司 + 关键人员 + 各核验状态）。found=false 表示暂无申请。
  // applicationId 可空：指定则回显该企业（多企业切换选中态），空则回显最优/最新。
  myApplication(applicationId?: string): Promise<MyApplicationReply> {
    const qs = applicationId ? `?applicationId=${encodeURIComponent(applicationId)}` : '';
    return firstValueFrom(
      this.http.get<MyApplicationReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/application${qs}`,
      ),
    );
  }

  // 列出本人名下全部企业（多企业切换器数据源）。
  listMyApplications(): Promise<ListMyApplicationsReply> {
    return firstValueFrom(
      this.http.get<ListMyApplicationsReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/applications`,
      ),
    );
  }

  // 某企业的业务活动（已开户企业才有：账户 + 近期交易流水）。
  enterpriseActivity(applicationId: string): Promise<EnterpriseActivityReply> {
    const qs = applicationId ? `?applicationId=${encodeURIComponent(applicationId)}` : '';
    return firstValueFrom(
      this.http.get<EnterpriseActivityReply>(
        `${customerBaseUrl()}/v1/customer/enterprise/activity${qs}`,
      ),
    );
  }

  // 平台支持的全部法币与稳定币（系统配置真值源）。
  supportedCurrencies(): Promise<SupportedCurrenciesReply> {
    return firstValueFrom(
      this.http.get<SupportedCurrenciesReply>(
        `${customerBaseUrl()}/v1/customer/reference/currencies`,
      ),
    );
  }

  // 可申請開通的收款幣種（後端取交集：settings enabled ∩ 通道商實際開通）。
  receivingCurrencies(): Promise<{ currencies?: ReceivingCurrency[] }> {
    return firstValueFrom(
      this.http.get<{ currencies?: ReceivingCurrency[] }>(
        `${customerBaseUrl()}/v1/customer/accounts/receiving-currencies`,
      ),
    );
  }

  // 申請開通某幣種的收款帳戶（冪等：同一 (客戶,幣種) 復用已開的帳戶）。
  /** 收款单据要求（哪笔钱卡住了、要补什么）。 */
  collectionAttachments(): Promise<{ attachments: CollectionAttachment[] }> {
    return firstValueFrom(
      this.http.get<{ attachments: CollectionAttachment[] }>(
        `${customerBaseUrl()}/v1/customer/collections/attachments`,
      ),
    );
  }

  /** 上传单据文件，返回 file_id。 */
  uploadFile(body: {
    name: string;
    content_type: string;
    content: string;
    biz_ref?: string;
    category?: string;
  }): Promise<{ fileId: string; name: string }> {
    return firstValueFrom(
      this.http.post<{ fileId: string; name: string }>(
        `${customerBaseUrl()}/v1/customer/files`,
        body,
      ),
    );
  }

  /** 提交某笔收款的贸易单据。 */
  submitCollectionAttachment(body: {
    order_id: string;
    attachment_type: string;
    files: { file_type: string; file_id: string }[];
  }): Promise<{ submitted: boolean }> {
    return firstValueFrom(
      this.http.post<{ submitted: boolean }>(
        `${customerBaseUrl()}/v1/customer/collections/attachments/submit`,
        body,
      ),
    );
  }

  /**
   * 查某种单据类型要交哪几份、哪些必填 —— 清单来自通道方，不在前端硬编码。
   *
   * 通道方逐个校验、一次只报一个缺失，少交一份就要白等一轮跨境上传才换回英文错误码；
   * 拿到清单才能在点提交之前就把话说清楚。返回空数组 = 通道方没给清单，不做前置校验。
   */
  describeAttachmentRequirements(
    attachmentType: string,
  ): Promise<{ requirements?: { fileType: string; required?: boolean; desc?: string }[] }> {
    return firstValueFrom(
      this.http.get<{ requirements?: { fileType: string; required?: boolean; desc?: string }[] }>(
        `${customerBaseUrl()}/v1/customer/collections/attachments/requirements`,
        { params: { attachment_type: attachmentType } },
      ),
    );
  }

  /** 收款账户开通进度（业务态；通道方退回时带原因）。 */
  provisioningStatus(): Promise<ProvisioningStatus> {
    return firstValueFrom(
      this.http.get<ProvisioningStatus>(
        `${customerBaseUrl()}/v1/customer/accounts/provisioning-status`,
      ),
    );
  }

  applyReceivingAccount(currency: string): Promise<ReceivingAccountReply> {
    return firstValueFrom(
      this.http.post<ReceivingAccountReply>(`${customerBaseUrl()}/v1/customer/accounts/apply`, {
        currency,
      }),
    );
  }

  // 上传证明文件（base64 经 customer→files 落库 + 登记到 identity 申请）；返回回显后的完整申请。
  uploadDocument(body: UploadDocBody): Promise<MyApplicationReply> {
    return firstValueFrom(
      this.http.post<MyApplicationReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/documents`,
        body,
      ),
    );
  }

  // 删除已上传的证明文件；返回回显后的完整申请。
  deleteDocument(body: DeleteDocBody): Promise<MyApplicationReply> {
    return firstValueFrom(
      this.http.post<MyApplicationReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/documents/delete`,
        body,
      ),
    );
  }

  // 读取证明文件内容（档案页预览：图片/PDF），返回 base64 + content_type。
  documentContent(fileId: string): Promise<DocumentContentReply> {
    return firstValueFrom(
      this.http.get<DocumentContentReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/documents/content?fileId=${encodeURIComponent(fileId)}`,
      ),
    );
  }

  // 可开户（公司注册）国家列表（后端可入驻配置；排除哪些国家由后端决定）。
  supportedCountries(): Promise<SupportedCountriesReply> {
    return firstValueFrom(
      this.http.get<SupportedCountriesReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/supported-countries`,
      ),
    );
  }

  // 按注册国 + 业务类型取该辖区证明文件清单（Kratos form codec 用 camelCase 查询参数）。
  documentRequirements(
    countryCode: string,
    businessType: number,
  ): Promise<DocumentRequirementsReply> {
    const qs = `countryCode=${encodeURIComponent(countryCode)}&businessType=${businessType}`;
    return firstValueFrom(
      this.http.get<DocumentRequirementsReply>(
        `${customerBaseUrl()}/v1/customer/onboarding/document-requirements?${qs}`,
      ),
    );
  }
}

// 按注册国动态文件清单（Kratos protojson 响应 camelCase）。
export interface DocRequirement {
  documentType?: string;
  displayName?: string;
  description?: string;
  required?: boolean;
  acceptedFormats?: string[];
}
// 按国动态字段提示（税号本地名等，后端 requirement_field 下发）。
export interface FieldHint {
  fieldKey?: string;
  label?: string;
  section?: string;
  required?: boolean;
  dataType?: string;
}
export interface DocumentRequirementsReply {
  found?: boolean;
  countryCode?: string;
  companyDocuments?: DocRequirement[];
  personDocuments?: DocRequirement[];
  fields?: FieldHint[];
}

// 可开户国家（后端下发）。
export interface SupportedCountry {
  code?: string;
  name?: string;
}
export interface SupportedCountriesReply {
  countries?: SupportedCountry[];
}

// 上传证明文件请求体（snake_case；Kratos protojson 输入）。
export interface UploadDocBody {
  scope: string; // company | person
  person_id: string; // scope=person 时必填
  document_type: string;
  display_name: string;
  file_name: string;
  content_type: string;
  content_base64: string;
}
export interface DeleteDocBody {
  scope: string;
  person_id: string;
  file_id: string;
}

// ── 回显响应（Kratos protojson：camelCase；int64 为字符串）──
export interface MyAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}
export interface MyCompany {
  legalName?: string;
  doingBusinessAs?: string;
  businessType?: number;
  incorporationCountry?: string;
  incorporationState?: string;
  incorporationDate?: string;
  registrationNumber?: string;
  taxIdentifiers?: {
    ein?: string;
    vatNumber?: string;
    unifiedSocialCreditCode?: string;
    businessRegistrationNumber?: string;
    otherTaxId?: string;
    otherTaxIdType?: string;
  };
  registeredAddress?: MyAddress;
  operatingAddress?: MyAddress;
  phone?: string;
  website?: string;
  supportEmail?: string;
  mcc?: string;
  industryCategory?: string;
  productDescription?: string;
  processingCountries?: string;
  volumeEstimate?: {
    currency?: string;
    monthlyVolumeMin?: string;
    monthlyVolumeMax?: string;
    averageTransactionAmount?: string;
    highestTransactionAmount?: string;
  };
  listedCompanyInfo?: { exchange?: string; ticker?: string; exempted?: boolean };
}
export interface CorporationDocument {
  documentType?: string;
  displayName?: string;
  state?: string; // required/uploaded/under_review/verified/rejected/expired
  fileId?: string;
  fileName?: string;
  rejectionReason?: string;
  uploadedAt?: string; // int64 序列化为字符串
}
export interface DocumentContentReply {
  contentType?: string;
  fileName?: string;
  downloadUrl?: string; // GCS/rustfs 直下签名 URL（host 已是 files.epinno.com），前端直连预览
  expiresAt?: number; // 签名过期 unix 秒
}
export interface MyApplicationOwner {
  owner?: {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    dateOfBirth?: string;
    nationality?: string;
    email?: string;
    phone?: string;
    title?: string;
    ownershipPercent?: number;
    isBeneficialOwner?: boolean;
    isDirector?: boolean;
    isLegalRepresentative?: boolean;
    isRepresentative?: boolean;
  };
  personId?: string;
  verificationStatus?: string; // verified/pending/failed/unverified
  documents?: CorporationDocument[];
}
export interface CompanyVerificationDims {
  legalEntity?: string;
  taxId?: string;
  address?: string;
  documents?: string;
  aml?: string;
  sanctions?: string;
}
export interface MyOwnershipNode {
  clientId?: string;
  kind?: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  personId?: string;
  country?: string;
  registrationNumber?: string;
  entityType?: string;
}
export interface MyOwnershipEdge {
  ownerClientId?: string;
  ownedClientId?: string;
  ownershipPercent?: number;
}
export interface MyApplicationReply {
  found?: boolean;
  applicationId?: string;
  applicationStatus?: string;
  company?: MyCompany;
  owners?: MyApplicationOwner[];
  noBeneficialOwners?: boolean;
  verification?: CompanyVerificationDims;
  ownershipNodes?: MyOwnershipNode[];
  ownershipEdges?: MyOwnershipEdge[];
  companyDocuments?: CorporationDocument[];
}

// 多企业切换器：单家企业摘要（Kratos protojson camelCase）。
export interface MyApplicationSummary {
  applicationId?: string;
  legalName?: string;
  applicationStatus?: string;
  incorporationCountry?: string;
  businessType?: number;
  customerId?: string;
  // role 当前用户在这家企业的角色：owner（资料管理员）/ admin / viewer。
  // 后端按【成员关系】下发——一个用户可以同时属于多家企业，且不一定是 owner。
  role?: 'owner' | 'admin' | 'viewer';
}
export interface ListMyApplicationsReply {
  applications?: MyApplicationSummary[];
}

// 企业业务活动（Kratos protojson camelCase；Money.amount 为字符串）。
export interface Money {
  currency?: string;
  amount?: string; // int64 最小单位，序列化为字符串
}
export interface ActivityAccount {
  id?: string;
  type?: string;
  balance?: Money;
  currency?: string; // 法币币种（VA）或稳定币资产（钱包）
  kind?: string; // settlement / va / wallet
  status?: string; // active / pending …
  chain?: string; // 稳定币所在链（钱包）
  label?: string; // 银行名（VA）/ 地址（钱包）
  // 汇款信息（VA）：不同渠道字段不同，有值才显示。
  accountNo?: string;
  holderName?: string;
  country?: string;
  swiftBic?: string;
  iban?: string;
  routingNumber?: string;
  sortCode?: string;
  bankAddress?: string;
}

export interface SupportedCurrency {
  code?: string;
  name?: string;
  kind?: string; // fiat / stablecoin
  chain?: string; // 稳定币链
}
export interface SupportedCurrenciesReply {
  fiat?: SupportedCurrency[];
  stablecoins?: SupportedCurrency[];
}
export interface EnterpriseActivityItem {
  accountId?: string;
  type?: string;
  amount?: Money;
  createdAt?: string; // int64 秒
  category?: string; // 业务类型：充值/提现/对换/收款
  direction?: string; // in（收入）/ out（支出）/ exchange（兑换）
  fee?: Money; // 手续费
  afterBalance?: Money; // 执行后账户余额
}
export interface EnterpriseActivityReply {
  provisioned?: boolean;
  accounts?: ActivityAccount[];
  items?: EnterpriseActivityItem[];
}

// ── 收款帳戶開通 ──────────────────────────────────────────────
// 客戶【不選銀行】：具體落到哪家銀行是通道商開通後才返回的。
// 幣種 = settings 裡 enabled 的法幣 ∩ 通道商實際開通的幣種（後端取交集）。
// ProvisioningStatus 收款账户开通进度（业务态）。
//
// 不出现「子商户」「unit」这类通道方内部概念 —— 客户只需要知道
// 「还缺什么」「在审核」「被退回了、原因是什么」「已开通」。
export interface ProvisioningStatus {
  status: 'materials_required' | 'under_review' | 'returned' | 'active' | 'none';
  returnReason?: string;
  missingMaterials?: string[];
  message?: string;
}

// CollectionAttachment 一笔收款的单据要求 —— 钱卡在账上，等单据。
export interface CollectionAttachment {
  orderId: string;
  attachmentType: string; // B2B / B2C / WEBSTORE / BankRequirement
  typeDesc?: string;
  state: 'required' | 'submitted' | 'approved' | 'rejected';
  auditRemark?: string; // 被拒时的原因 —— 客户必须看得到
}

export interface ReceivingCurrency {
  code: string;
  name: string;
  country: string;
}
export interface ReceivingAccountReply {
  currency: string;
  status: string;
  accountRef?: string;
  bankName?: string;
  accountNumber?: string;
  swift?: string;
}
